import '../../styles/restScreens.css';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabaseClient } from '../../config/supabase';
import EntityFormModal from './EntityFormModal';
import OrgGroupFormModal from './OrgGroupFormModal';
import BrandingPanel from './BrandingPanel';
import CompanyLogoPanel from './CompanyLogoPanel';
import { Link } from 'react-router-dom';
import { statusMeta } from './entityConstants';
import { DEFAULT_PALETTE } from '../../config/peacockPalette';
import useModuleAccess from '../../hooks/useModuleAccess';
import SignInWelcomeSetting from './SignInWelcomeSetting';
import WhiteLabelEmailSetting from './WhiteLabelEmailSetting';

const paletteFields = [
    'canvas_light', 'canvas_dark',
    'surface_light', 'surface_dark',
    'nav_light', 'nav_dark',
    'primary', 'secondary', 'depth', 'border',
    'excellent', 'good', 'caution', 'moderate', 'critical', 'neutral'
];

const normalizePalette = (palette) => {
    const out = {};
    paletteFields.forEach((key) => {
        const value = palette?.[key];
        if (typeof value === 'string' && value.trim()) out[key] = value.trim();
    });
    return out;
};

const resolveStoredPalette = (palette) => ({ ...DEFAULT_PALETTE, ...normalizePalette(palette) });

const collectDescendantIds = (childrenOf, entityId) => {
    const out = [];
    (childrenOf[entityId] || []).forEach((child) => {
        out.push(child.id, ...collectDescendantIds(childrenOf, child.id));
    });
    return out;
};

const collectGroupDescendantIds = (childrenOf, groupId) => {
    const out = [];
    (childrenOf[groupId] || []).forEach((child) => {
        out.push(child.id, ...collectGroupDescendantIds(childrenOf, child.id));
    });
    return out;
};

// Module 1 — the Organization screen.
//
// Val, 2026-07-17: Organization is a CONTAINER, and its sections should each say what they are
// for. One flat tree+form used to do three unrelated jobs with no labels — the field that sets
// your account-number prefix sat in the same box as a colour swatch, and nothing explained
// either. Now three self-describing sections:
//   • STRUCTURE  — the tree: who sits under whom, folder vs front desk, create/edit/archive.
//   • NUMBERING  — the entity codes (the ATL in ATL-00001), with the warning that changing a
//                  code never renumbers accounts already issued.
//   • BRANDING   — the per-compartment accent colour, read-only until you click Edit.
//
// BL-392: `mode` splits the screen between its two homes. 'brands' (Admin → Brands & Locations)
// manages Companies, Markets, numbering, logos, and colours; 'departments' (Staff & Roles →
// Structure) shows the same tree with Team, Group, and Department controls. 'all' keeps both.
function EntitiesView({ userId, theme = 'light', onEntitiesChanged, mode = 'all' }) {
    const dark = theme === 'dark';
    const showDepartments = mode !== 'brands';
    const manageEntities = mode !== 'departments';

    const [entities, setEntities] = useState([]);
    const [orgGroups, setOrgGroups] = useState([]);
    const [staff, setStaff] = useState([]);
    const [groupEditAccess, setGroupEditAccess] = useState({});
    const [canEditAllOrgGroups, setCanEditAllOrgGroups] = useState(false);
    const [engCountByEntity, setEngCountByEntity] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const [showForm, setShowForm] = useState(false);
    const [formMode, setFormMode] = useState('create');
    const [editing, setEditing] = useState(null);
    const [showGroupForm, setShowGroupForm] = useState(false);
    const [groupFormMode, setGroupFormMode] = useState('create');
    const [editingGroup, setEditingGroup] = useState(null);
    const [groupInitial, setGroupInitial] = useState({ entity_id: '', parent_group_id: '' });

    const [open, setOpen] = useState({ structure: true, numbering: false, logos: mode === 'brands', branding: false });
    const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));
    const { allowed: canManage, loading: accessLoading } = useModuleAccess('organization', 'manage');
    const { loading: personnelAccessLoading } = useModuleAccess('personnel', 'view');

    // ---- Data ---------------------------------------------------------------
    const loadEntities = useCallback(async () => {
        setLoadError(null);
        const [entitiesResult, groupsResult, staffResult] = await Promise.all([
            supabaseClient.from('entities').select('*').order('created_at'),
            supabaseClient
                .from('org_groups')
                .select('id, org_id, entity_id, parent_group_id, group_type, name, code, lead_staff_id, created_at, created_by, updated_at, updated_by')
                .order('created_at'),
            supabaseClient
                .from('staff')
                .select('id, entity_id, group_id, badge, title, status, user_profile_id')
                .order('created_at')
        ]);
        const error = entitiesResult.error || groupsResult.error || staffResult.error;
        if (error) {
            setLoadError(error.message);
            setEntities([]);
            setOrgGroups([]);
            setStaff([]);
            setGroupEditAccess({});
            setCanEditAllOrgGroups(false);
        } else {
            const allEntities = entitiesResult.data || [];
            const allGroups = groupsResult.data || [];
            const allStaff = staffResult.data || [];
            const [fullViewResult, fullEditResult, fullOrganizationViewResult, orgAdminResult] = await Promise.all([
                supabaseClient.rpc('has_module_access', {
                    p_module: 'personnel',
                    p_action: 'view',
                    p_entity_id: null
                }),
                supabaseClient.rpc('has_module_access', {
                    p_module: 'personnel',
                    p_action: 'edit',
                    p_entity_id: null
                }),
                supabaseClient.rpc('has_module_access', {
                    p_module: 'organization',
                    p_action: 'view',
                    p_entity_id: null
                }),
                supabaseClient.rpc('is_org_admin')
            ]);
            if (orgAdminResult.error) {
                setLoadError(orgAdminResult.error.message);
                setLoading(false);
                return;
            }
            const hasFullPersonnelView = !fullViewResult.error && fullViewResult.data === true;
            const hasFullOrganizationView = !fullOrganizationViewResult.error && fullOrganizationViewResult.data === true;
            const canEditGroups = orgAdminResult.data === true;
            const hasFullEdit = canEditGroups
                && !fullEditResult.error && fullEditResult.data === true;
            const editAccessByGroup = {};
            let visibleGroups = allGroups;
            let visibleEntities = allEntities;

            if (hasFullPersonnelView) {
                allGroups.forEach((group) => { editAccessByGroup[group.id] = hasFullEdit; });
            } else {
                const groupAccessResults = await Promise.all(allGroups.map(async (group) => {
                    const [viewResult, editResult] = await Promise.all([
                        supabaseClient.rpc('has_module_access', {
                            p_module: 'personnel',
                            p_action: 'view',
                            p_entity_id: group.entity_id,
                            p_group_id: group.id
                        }),
                        supabaseClient.rpc('has_module_access', {
                            p_module: 'personnel',
                            p_action: 'edit',
                            p_entity_id: group.entity_id,
                            p_group_id: group.id
                        })
                    ]);
                    return {
                        group,
                        canView: !viewResult.error && viewResult.data === true,
                        canEdit: canEditGroups && !editResult.error && editResult.data === true
                    };
                }));
                const personnelVisibleGroups = groupAccessResults.filter((result) => result.canView).map((result) => result.group);
                groupAccessResults.forEach((result) => {
                    if (result.canView) editAccessByGroup[result.group.id] = result.canEdit;
                });

                if (!hasFullOrganizationView && personnelVisibleGroups.length > 0) {
                    visibleGroups = personnelVisibleGroups;
                    const entityById = new Map(allEntities.map((entity) => [entity.id, entity]));
                    const visibleEntityIds = new Set();
                    visibleGroups.forEach((group) => {
                        let entity = entityById.get(group.entity_id);
                        while (entity) {
                            visibleEntityIds.add(entity.id);
                            entity = entity.parent_entity_id ? entityById.get(entity.parent_entity_id) : null;
                        }
                    });
                    visibleEntities = allEntities.filter((entity) => visibleEntityIds.has(entity.id));
                }
            }

            setEntities(visibleEntities);
            setOrgGroups(visibleGroups);
            setStaff(allStaff.filter((member) => visibleEntities.some((entity) => entity.id === member.entity_id)));
            setGroupEditAccess(editAccessByGroup);
            setCanEditAllOrgGroups(hasFullEdit);
        }
        setLoading(false);
    }, []);

    const loadEngagementCounts = useCallback(async () => {
        const { data, error } = await supabaseClient.from('engagements').select('entity_id');
        if (!error) {
            const counts = {};
            (data || []).forEach((row) => {
                if (row.entity_id) counts[row.entity_id] = (counts[row.entity_id] || 0) + 1;
            });
            setEngCountByEntity(counts);
        }
    }, []);

    useEffect(() => {
        loadEntities();
        loadEngagementCounts();
    }, [loadEntities, loadEngagementCounts, userId]);

    // ---- Tree helpers -------------------------------------------------------
    const childrenOf = useMemo(() => {
        const map = {};
        entities.forEach((e) => {
            const key = e.parent_entity_id || '__root__';
            (map[key] = map[key] || []).push(e);
        });
        return map;
    }, [entities]);

    const groupChildrenOf = useMemo(() => {
        const map = {};
        orgGroups.forEach((group) => {
            const key = group.parent_group_id || `entity:${group.entity_id}`;
            (map[key] = map[key] || []).push(group);
        });
        return map;
    }, [orgGroups]);

    const rolledCount = useCallback((id) => {
        let total = engCountByEntity[id] || 0;
        (childrenOf[id] || []).forEach((child) => { total += rolledCount(child.id); });
        return total;
    }, [engCountByEntity, childrenOf]);

    const descendantIds = useCallback((id) => collectDescendantIds(childrenOf, id), [childrenOf]);
    const groupDescendantIds = useCallback((id) => collectGroupDescendantIds(groupChildrenOf, id), [groupChildrenOf]);

    const pushPaletteToDescendants = useCallback(async (sourceEntityId) => {
        const { data: sourceEntity, error: sourceError } = await supabaseClient
            .from('entities')
            .select('id, org_id, color_palette')
            .eq('id', sourceEntityId)
            .single();
        if (sourceError) throw sourceError;

        const descendantEntityIds = descendantIds(sourceEntityId);
        if (descendantEntityIds.length === 0) return 0;

        const resolvedPalette = resolveStoredPalette(sourceEntity?.color_palette);
        const updates = descendantEntityIds.map((id) => ({
            id,
            color_palette: resolvedPalette,
            updated_by: userId
        }));

        const chunkSize = 25;
        for (let i = 0; i < updates.length; i += chunkSize) {
            const chunk = updates.slice(i, i + chunkSize);
            const { error } = await supabaseClient.from('entities').upsert(chunk, { onConflict: 'id' });
            if (error) throw error;
        }

        await loadEntities();
        if (onEntitiesChanged) onEntitiesChanged();
        return descendantEntityIds.length;
    }, [descendantIds, loadEntities, onEntitiesChanged, userId]);

    // ---- Mutations ----------------------------------------------------------
    const createEntity = async (payload) => {
        const orgId = entities.find((item) => item.id === payload.parent_entity_id)?.org_id || entities[0]?.org_id;
        const { data, error } = await supabaseClient.from('entities').insert([{ ...payload, org_id: orgId, created_by: userId }]).select().single();
        if (error) throw error;
        await loadEntities();
        if (onEntitiesChanged) onEntitiesChanged(data);
        setShowForm(false);
    };
    const updateEntity = async (payload) => {
        const { data, error } = await supabaseClient.from('entities').update({ ...payload, updated_by: userId }).eq('id', editing.id).select().single();
        if (error) throw error;
        await loadEntities();
        if (onEntitiesChanged) onEntitiesChanged(data);
        setShowForm(false);
        setEditing(null);
    };
    const createOrgGroup = async (payload) => {
        const entity = entities.find((candidate) => candidate.id === payload.entity_id);
        const { data, error } = await supabaseClient
            .from('org_groups')
            .insert([{ ...payload, org_id: entity?.org_id, created_by: userId }])
            .select()
            .single();
        if (error) throw error;
        await loadEntities();
        if (onEntitiesChanged) onEntitiesChanged(data);
        setShowGroupForm(false);
    };
    const updateOrgGroup = async (payload) => {
        const { data, error } = await supabaseClient
            .from('org_groups')
            .update({ ...payload, updated_by: userId })
            .eq('id', editingGroup.id)
            .select()
            .single();
        if (error) throw error;
        await loadEntities();
        if (onEntitiesChanged) onEntitiesChanged(data);
        setShowGroupForm(false);
        setEditingGroup(null);
    };
    const archiveOrgGroup = async (group) => {
        if ((groupChildrenOf[group.id] || []).length > 0) {
            window.alert('Archive the child Teams, Groups, or Departments first, then archive this one.');
            return;
        }
        if (!window.confirm(`Archive "${group.name}"? It will drop off the active org-structure tree.`)) return;
        const { error } = await supabaseClient.from('org_groups').delete().eq('id', group.id);
        if (error) { alert('Could not archive: ' + error.message); return; }
        await loadEntities();
        if (onEntitiesChanged) onEntitiesChanged();
    };
    const archiveEntity = async (entity) => {
        if (!window.confirm(`Archive "${entity.name}"? It stays as history but drops off the active tree.`)) return;
        const { error } = await supabaseClient.from('entities').update({ status: 'archived', updated_by: userId }).eq('id', entity.id);
        if (error) { alert('Could not archive: ' + error.message); return; }
        loadEntities();
        if (onEntitiesChanged) onEntitiesChanged();
    };

    // Branding save — persists the colour and routes through onEntitiesChanged, the same path
    // that re-skins the app on save (CRMApp.handleBrandsChanged). Does NOT depend on the form's
    // `editing` state, so the Branding panel can save any compartment directly.
    const saveColor = async (entity, hexOrNull) => {
        const color_palette = hexOrNull
            ? { ...(entity.color_palette || {}), primary: hexOrNull }
            : null;
        const { data, error } = await supabaseClient
            .from('entities')
            .update({ color_palette, updated_by: userId })
            .eq('id', entity.id).select().single();
        if (error) throw error;
        await loadEntities();
        if (onEntitiesChanged) onEntitiesChanged(data);
    };
    
    const applyPaletteToSubEntities = async (entity) => {
        const targetCount = descendantIds(entity.id).length;
        if (targetCount === 0) {
            window.alert('There are no child Companies/Markets under this record.');
            return;
        }
        if (!window.confirm(`Apply "${entity.name}"'s current palette to ${targetCount} sub-entities? This copies the resolved palette into each descendant.`)) return;
        try {
            await pushPaletteToDescendants(entity.id);
        } catch (error) {
            alert('Could not push palette: ' + (error?.message || 'Unknown error'));
        }
    };

    const openCreate = (parent = null) => { setFormMode('create'); setEditing(parent ? { parent_entity_id: parent.id } : null); setShowForm(true); };
    const openEdit = (entity) => { setFormMode('edit'); setEditing(entity); setShowForm(true); };
    const openCreateGroupUnderEntity = (entity) => {
        setGroupFormMode('create');
        setEditingGroup(null);
        setGroupInitial({ entity_id: entity.id, parent_group_id: '' });
        setShowGroupForm(true);
    };
    const openCreateGroupUnderGroup = (group) => {
        setGroupFormMode('create');
        setEditingGroup(null);
        setGroupInitial({ entity_id: group.entity_id, parent_group_id: group.id });
        setShowGroupForm(true);
    };
    const openEditGroup = (group) => {
        setGroupFormMode('edit');
        setEditingGroup(group);
        setGroupInitial({ entity_id: group.entity_id, parent_group_id: group.parent_group_id || '' });
        setShowGroupForm(true);
    };

    // ---- Styles -------------------------------------------------------------
    const cardCls = 'bg-[var(--surface-raised)] border-[var(--surface-border)]';
    const StatusPill = ({ status }) => {
        const m = statusMeta(status);
        return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${m.pill}`}>{m.label}</span>;
    };

    // ---- Recursive node render (STRUCTURE) ---------------------------------
    const renderGroupNode = (group, depth) => {
        const kids = groupChildrenOf[group.id] || [];
        const lead = staff.find((member) => member.id === group.lead_staff_id);
        const canEditGroup = canEditAllOrgGroups || groupEditAccess[group.id];
        return (
            <div key={group.id}>
                <div
                    className={`flex items-center gap-3 py-2.5 px-3 rounded-lg border border-dashed ${dark ? 'border-blue-800/70 hover:bg-blue-950/30' : 'border-blue-100 hover:bg-blue-50'}`}
                    style={{ marginLeft: depth * 24 }}
                >
                    <span className="text-lg" title={group.group_type || 'Department'}>👥</span>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={`font-semibold ${dark ? 'text-blue-100' : 'text-blue-900'}`}>{group.name}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${dark ? 'bg-blue-900/60 text-blue-200' : 'bg-blue-100 text-blue-700'}`}>{group.group_type || 'Department'}</span>
                            {group.code && <span className={`text-xs font-mono px-2 py-0.5 rounded ${dark ? 'bg-gray-900 text-gray-200 border border-gray-700' : 'bg-gray-100 text-gray-700 border border-gray-300'}`}>{group.code}</span>}
                            {lead && <span className={`text-xs px-2 py-0.5 rounded ${dark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>Lead: {lead.title || lead.id}</span>}
                        </div>
                    </div>
                    {canEditGroup && (
                        <div className="flex items-center gap-1">
                            <button onClick={() => openCreateGroupUnderGroup(group)} className={`px-2 py-1 rounded text-xs font-medium ${dark ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-600 hover:bg-gray-100'}`}>＋ Child group</button>
                            <button onClick={() => openEditGroup(group)} className={`px-2 py-1 rounded text-xs font-medium ${dark ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-600 hover:bg-gray-100'}`}>Edit</button>
                            <button onClick={() => archiveOrgGroup(group)} className="px-2 py-1 rounded text-xs font-medium text-red-600 hover:bg-red-50">Archive</button>
                        </div>
                    )}
                </div>
                {kids.map((child) => renderGroupNode(child, depth + 1))}
            </div>
        );
    };

    const renderNode = (entity, depth) => {
        const kids = childrenOf[entity.id] || [];
        const groupRoots = groupChildrenOf[`entity:${entity.id}`] || [];
        const count = rolledCount(entity.id);
        return (
            <div key={entity.id}>
                <div
                    className={`flex items-center gap-3 py-2.5 px-3 rounded-lg border ${dark ? 'border-gray-700 hover:bg-gray-700/40' : 'border-gray-100 hover:bg-brand-50'}`}
                    style={{ marginLeft: depth * 24 }}
                >
                    <span className="text-lg" title={entity.is_booking_node ? 'Front desk — issues engagements' : 'Folder — groups Companies/Markets'}>{entity.is_booking_node ? '🏷️' : '📁'}</span>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={`font-semibold ${dark ? 'text-gray-100' : 'text-gray-800'}`}>{entity.name}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${dark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{entity.level_type === 'organization' ? 'Organization' : entity.level_type === 'market' ? 'Market' : 'Company'}</span>
                            {entity.entity_code && <span className="text-xs font-mono px-2 py-0.5 rounded bg-brand-100 text-brand-700 border border-brand-200">{entity.entity_code}</span>}
                            <StatusPill status={entity.status} />
                        </div>
                    </div>
                    <div className={`text-xs whitespace-nowrap ${dark ? 'text-gray-400' : 'text-gray-500'}`} title="Engagements in this box + everything beneath it">
                        {count} engagement{count === 1 ? '' : 's'}
                    </div>
                    <div className="flex items-center gap-1">
                        {manageEntities && !accessLoading && canManage && (
                            <>
                                {entity.level_type !== 'market' && <button onClick={() => openCreate(entity)} className={`px-2 py-1 rounded text-xs font-medium ${dark ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-600 hover:bg-gray-100'}`}>{entity.level_type === 'organization' ? '＋ Company' : '＋ Market'}</button>}
                                <button onClick={() => applyPaletteToSubEntities(entity)} className={`px-2 py-1 rounded text-xs font-medium ${dark ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-600 hover:bg-gray-100'}`}>Apply to all sub-entities</button>
                                <button onClick={() => openEdit(entity)} className={`px-2 py-1 rounded text-xs font-medium ${dark ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-600 hover:bg-gray-100'}`}>Edit</button>
                                {entity.status !== 'archived' && (
                                    <button onClick={() => archiveEntity(entity)} className="px-2 py-1 rounded text-xs font-medium text-red-600 hover:bg-red-50">Archive</button>
                                )}
                            </>
                        )}
                        {showDepartments && canEditAllOrgGroups && entity.level_type !== 'organization' && <button onClick={() => openCreateGroupUnderEntity(entity)} className={`px-2 py-1 rounded text-xs font-medium ${dark ? 'text-blue-200 hover:bg-gray-600' : 'text-blue-700 hover:bg-blue-50'}`}>＋ Team / Group / Department</button>}
                    </div>
                </div>
                {showDepartments && groupRoots.map((group) => renderGroupNode(group, depth + 1))}
                {kids.map((child) => renderNode(child, depth + 1))}
            </div>
        );
    };

    const roots = childrenOf['__root__'] || [];
    const bookingNodes = entities.filter((e) => e.is_booking_node && e.status !== 'archived');

    // ---- A self-describing, collapsible section -----------------------------
    const Section = ({ id, icon, title, description, children }) => {
        const isOpen = open[id];
        return (
            <div className={`rounded-xl border ${cardCls}`}>
                <button onClick={() => toggle(id)} className="w-full flex items-center gap-3 p-4 text-left">
                    <span className="text-2xl">{icon}</span>
                    <span className="flex-1">
                        <span className={`block font-bold ${dark ? 'text-gray-100' : 'text-gray-800'}`}>{title}</span>
                        <span className={`block text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>{description}</span>
                    </span>
                    <span className={`transition-transform ${isOpen ? 'rotate-180' : ''} ${dark ? 'text-gray-500' : 'text-gray-400'}`}>▼</span>
                </button>
                {isOpen && <div className="px-4 pb-4">{children}</div>}
            </div>
        );
    };

    if (loading || personnelAccessLoading) return <div data-m6-layout="entities" data-m6-component="EntitiesView" className="p-10 text-center text-gray-500">Loading the organization…</div>;
    if (loadError) return <div data-m6-layout="entities" data-m6-component="EntitiesView" className="p-6 text-center text-red-600">Could not load: {loadError}</div>;

    return (
        <div data-m6-layout="entities" data-m6-component="EntitiesView" className="h-full flex flex-col">
            <div className="mb-4">
                <h1 className={`text-2xl font-bold ${'text-[var(--ink)]'}`}>{mode === 'brands' ? 'Brands & Locations' : mode === 'departments' ? 'Teams, Groups & Departments' : 'Companies, Markets & Departments'}</h1>
                <p className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {mode === 'brands'
                        ? 'Organization → Company → Market/Branch, with each Company\'s logo, brand name, and colours.'
                        : 'Organization → Company → Market/Branch → Team → Group → Department → Title → Role Tag.'}
                </p>
                {mode === 'departments' && <Link to="/admin/organization" className="mt-1 inline-block text-sm font-medium text-[var(--auth-pill-blue)] underline">Manage Companies &amp; Markets → Brands &amp; Locations</Link>}
            </div>

            <div className="flex-1 overflow-auto space-y-3">
                {/* STRUCTURE */}
                <Section id="structure" icon="🌳" title="Structure"
                    description={mode === 'brands' ? 'Companies and Markets: who sits under whom and which locations take bookings.'
                        : mode === 'departments' ? 'Teams, Groups, and Departments under each Company or Market.'
                            : 'Companies, Markets, Teams, Groups, and Departments: who sits under whom and which locations take bookings.'}>
                    <div className="flex justify-end mb-3">
                    {manageEntities && !accessLoading && canManage && <button onClick={() => openCreate(entities.find((item) => item.level_type === 'organization'))} className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 font-medium">New Company</button>}
                  </div>
                    {roots.length === 0 ? (
                        <div className="p-10 text-center">
                            <div className="text-4xl mb-2">🏛️</div>
                            <div className={`font-medium ${dark ? 'text-gray-200' : 'text-gray-700'}`}>No Companies or Markets yet</div>
                            <div className={`text-sm mb-4 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>Create a Company under your Organization.</div>
                        </div>
                    ) : (
                        <div className="space-y-1">{roots.map((r) => renderNode(r, 0))}</div>
                    )}
                    <p className={`mt-3 text-xs ${dark ? 'text-gray-500' : 'text-gray-400'}`}>{mode === 'brands' ? 'Markets sit under a Company.' : 'Markets sit under a Company. Teams and Groups roll into Departments under each Company or Market.'} Engagement counts include everything underneath.</p>
                </Section>

                {manageEntities && <>
                {/* NUMBERING */}
                <Section id="numbering" icon="🔢" title="Numbering"
                    description="The Company/Market code that starts account numbers — the ATL in ATL-00001.">
                    {bookingNodes.length === 0 ? (
                        <div className={`text-sm p-4 text-center ${dark ? 'text-gray-500' : 'text-gray-400'}`}>No booking Companies/Markets yet. Only booking locations issue numbered accounts.</div>
                    ) : (
                        <div className="space-y-2">
                            {bookingNodes.map((e) => (
                                <div key={e.id} className={`flex items-center gap-3 py-2.5 px-3 rounded-lg border ${dark ? 'border-gray-700' : 'border-gray-200'}`}>
                                    <span className="text-xs font-mono px-2 py-0.5 rounded bg-brand-100 text-brand-700 border border-brand-200">{e.entity_code || '—'}</span>
                                    <div className="flex-1 min-w-0">
                                        <div className={`font-semibold ${dark ? 'text-gray-100' : 'text-gray-800'}`}>{e.name}</div>
                                        <div className={`text-xs font-mono ${dark ? 'text-gray-400' : 'text-gray-500'}`}>{e.entity_code ? `${e.entity_code}-00001, ${e.entity_code}-00002, …` : 'No code set'}</div>
                                    </div>
                                    {!accessLoading && canManage && <button onClick={() => openEdit(e)} className={`px-3 py-1.5 rounded text-sm font-medium border ${dark ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>Edit</button>}
                                </div>
                            ))}
                        </div>
                    )}
                    <p className={`mt-3 text-xs ${dark ? 'text-amber-400/80' : 'text-amber-600'}`}>⚠️ Changing a code only affects <em>new</em> accounts. Numbers already issued (like ATL-00001) keep the code they were born with — they are never renumbered.</p>
                </Section>

                {/* BRANDING */}
                <Section id="branding" icon="🎨" title="Branding"
                    description="The colour each Company/Market wears. Everyone who logs in sees it.">
                    <BrandingPanel entities={entities} theme={theme} onSaveColor={saveColor} />
                    <WhiteLabelEmailSetting theme={theme} />
                </Section>

                {mode === 'brands' && (
                    <Section id="logos" icon="🖼️" title="Logos & brand names"
                        description="The logo and name at the top of invite, code, and password emails.">
                        <CompanyLogoPanel entities={entities} onChanged={async (data) => {
                            await loadEntities();
                            if (onEntitiesChanged) onEntitiesChanged(data);
                        }} />
                    </Section>
                )}

                {mode === 'brands' && (
                    <Section id="signin" icon="🎬" title="Sign-in welcome animation"
                        description="What plays after someone signs in, while Central Command loads.">
                        <SignInWelcomeSetting theme={theme} canManage={!accessLoading && canManage} />
                    </Section>
                )}
                </>}
            </div>

            {showForm && (
                <EntityFormModal
                    mode={formMode}
                    entity={editing}
                    entities={entities}
                    descendantIds={formMode === 'edit' && editing ? descendantIds(editing.id) : []}
                    onClose={() => { setShowForm(false); setEditing(null); }}
                    onSave={formMode === 'edit' ? updateEntity : createEntity}
                    theme={theme}
                />
            )}
            {showGroupForm && (
                <OrgGroupFormModal
                    mode={groupFormMode}
                    group={editingGroup}
                    entities={entities}
                    groups={orgGroups}
                    staff={staff}
                    descendantIds={groupFormMode === 'edit' && editingGroup ? groupDescendantIds(editingGroup.id) : []}
                    initialEntityId={groupInitial.entity_id}
                    initialParentGroupId={groupInitial.parent_group_id}
                    lockPlacement={!canEditAllOrgGroups}
                    canEditCode={canEditAllOrgGroups}
                    onClose={() => { setShowGroupForm(false); setEditingGroup(null); }}
                    onSave={groupFormMode === 'edit' ? updateOrgGroup : createOrgGroup}
                    theme={theme}
                />
            )}
        </div>
    );
}

export default EntitiesView;
